import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../context/AuthContext';
import { useNotification } from '../../../../context/NotificationContext';
import { NotificationType, Position } from '../../../../types/enums/notificationEnums';
import { useCountdown } from '../../../../context/CountdownContext';

const useRegisterPageLogic = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [modalState, setModalState] = useState({
    isPrivacyPolicyModalOpen: false,
    isTermsOfUseModalOpen: false,
    isVerificationModalOpen: false
  });
  const [errorMessages, setErrorMessages] = useState<{ [key: string]: string }>({});
  const [agreement, setAgreement] = useState(false);
  const navigate = useNavigate();
  const { register, loadingStates, googleLogin } = useAuth();
  const { countdownTime, isCountdownActive, startCountdown, resetCountdown } = useCountdown();
  const [resend, setResend] = useState({
    email: email,
    password: password,
    confirmPassword: confirmPassword,
    agreement: agreement
  });

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAgreement(false);
    setErrorMessages({});
  };

  const handleModalToggle = (modalName: string, isOpen: boolean) => {
    setModalState(prevState => ({
      ...prevState,
      [modalName]: isOpen
    }));
  };

  const validateForm = (
    email: string,
    password: string,
    confirmPassword: string,
    agreement: boolean
  ) => {
    const errors: { [key: string]: string } = {};
    if (!email) errors.email = t('Email is required');
    if (!password) errors.password = t('Password is required');
    if (!confirmPassword) errors.confirmPassword = t('Please enter your password again');
    if (password !== confirmPassword) errors.confirmPassword = t('Passwords do not match');

    if (email && password && confirmPassword && password === confirmPassword && !agreement) {
      errors.agreement = t('You must agree to the terms of use and privacy policy');
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validateForm(email, password, confirmPassword, agreement);
    setErrorMessages(errors);

    if (Object.keys(errors).length === 0) {
      if (!isCountdownActive) {
        await performRegistration();
      } else {
        setModalState({ ...modalState, isVerificationModalOpen: true });
      }
    }
  };

  const performRegistration = async () => {
    try {
      setResend({ email, password, confirmPassword, agreement });
      const errorMessage = await register(email, password, confirmPassword);
      if (errorMessage) {
        setErrorMessages({ general: t(errorMessage) });
      } else {
        // Email verification is still sent, but we don't show the modal
        // Just show a notification that registration was successful
        showNotification(
          t('Registration successful!'),
          NotificationType.Success,
          Position.TopRight
        );

        // Still start the countdown in case we need it later
        startCountdown(20);

        // Optionally navigate the user to login or home page
        navigate('/');

        resetForm();
      }
    } catch (error) {
      showNotification(
        t('An error occurred while sending the registration link.'),
        NotificationType.Error,
        Position.TopRight
      );
    }
  };

  const handleResendEmail = async () => {
    if (!isCountdownActive) {
      const errors = validateForm(
        resend.email,
        resend.password,
        resend.confirmPassword,
        resend.agreement
      );
      setErrorMessages(errors);

      if (Object.keys(errors).length === 0) {
        const errorMessage = await register(resend.email, resend.password, resend.confirmPassword);
        if (errorMessage) {
          setErrorMessages({ ...errors, general: t(errorMessage) });
        } else {
          startCountdown(20);
          setModalState({ ...modalState, isVerificationModalOpen: true });
        }
      }
    }
  };
  const handleCountdownEnd = () => {
    resetCountdown();
  };

  const handleAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreement(e.target.checked);
    setErrorMessages(prevErrors => {
      const { agreement, ...rest } = prevErrors;
      return rest;
    });
  };

  const handleGoogleSignInClick = () => {
    googleLogin();
  };

  const handleAppleSignInClick = () => {};

  const fields = [
    {
      name: 'email',
      type: 'email',
      label: t('Email'),
      placeholder: t('Email'),
      id: 'register-email',
      htmlFor: 'register-email',
      value: email,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)
    },
    {
      name: 'password',
      type: 'password',
      label: t('Password'),
      placeholder: t('Password'),
      id: 'register-password',
      htmlFor: 'register-password',
      value: password,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)
    },
    {
      name: 'confirmPassword',
      type: 'password',
      label: t('Confirm Password'),
      placeholder: t('Confirm Password'),
      id: 'register-confirmPassword',
      htmlFor: 'register-confirmPassword',
      value: confirmPassword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)
    }
  ];

  return {
    t,
    email,
    password,
    confirmPassword,
    modalState,
    errorMessages,
    agreement,
    loadingStates,
    handleModalToggle,
    handleSubmit,
    handleAgreementChange,
    handleGoogleSignInClick,
    handleAppleSignInClick,
    fields,
    countdownTime,
    handleResendEmail,
    isCountdownActive,
    handleCountdownEnd,
    resetCountdown
  };
};

export default useRegisterPageLogic;
